import { CompactUserResponse, UserId, UserResponse } from '@business/entities/account';
import { TeamId, TeamResponse } from '@business/entities/team';
import { CommonService } from '@business/services';
import { FieldProps, FormControl, useFormField } from '@core/utils/form';
import { R } from '@core/utils/r';
import FormHelperText from '@material-ui/core/FormHelperText';
import { AvatarGroup, AvatarGroupItem } from '@modules/common/AvatarGroup';
import { GCDropdown } from '@modules/common/GCDropdown';
import React, { CSSProperties, useMemo } from 'react';
import Select, { StylesConfig } from 'react-select';

const selectStyles: StylesConfig<any, false> = {
  control: provided => ({
    ...provided,
    minWidth: 240,
    margin: 8,
  }),
  menu: () => ({ boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)' }),
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface FormUserAvatarBaseProps<T extends (UserId | TeamId)[]> {
  onChange?(value: T | null): void;
  autofocus?: boolean;
  style?: CSSProperties;
  type?: 'user' | 'team' | 'both';
}

export interface FormUserAvatarProps<T extends (UserId | TeamId)[]>
  extends FormUserAvatarBaseProps<UserId[] | TeamId[]>,
    FieldProps<FormControl<UserId[] | TeamId[]>> {
  users: (UserResponse | CompactUserResponse)[];
  teams?: TeamResponse[];
  onAdd?(value: string): void;
  tooltipContainer?: string;
  maxSize?: number;
  meId?: string;
  viewOnly?: boolean;
  additionalOptions?: { label: string; value: string; id: string }[];
  addMe?: boolean;
  closeOnSelect?: boolean;
  disabled?: boolean;
}

export function FormUserAvatar(props: FormUserAvatarProps<UserId[]>) {
  const { type = 'user', teams = [], additionalOptions = [], addMe = true, closeOnSelect = false } = props;
  const { formControl, fieldState } = useFormField(props);
  const fieldTouched = (fieldState && fieldState.touched) || formControl.inheritedSubmitted || false;
  const fieldError = (fieldTouched && fieldState.errorMessage) || null;
  const meId = props.meId;
  const fieldValue = (R.isNil(fieldState.value) ? [] : R.filter(fieldState.value, u => u != 0)) as UserId[];
  const canAdd = useMemo(() => !props.viewOnly, [props.viewOnly]);

  if (meId && fieldValue.length === 0 && addMe) {
    fieldValue.push(meId);
  }

  const users = useMemo(() => {
    const mappedUsers = R.map(props.users, u => {
      if (u.id == meId) {
        return { ...u, firstName: 'Me', lastName: ' ' };
      }
      return u;
    });
    return R.sortBy(mappedUsers, u => (u.id == meId ? '1' : CommonService.getFullName(u)), 'asc');
  }, [props.users, meId]);

  const selectedItems: AvatarGroupItem[] = useMemo(() => {
    return R.pipe(
      fieldValue,
      R.ops.map(id => {
        const additioanlItem = R.find(additionalOptions, o => o.value === id);
        if (additioanlItem) return additioanlItem;

        if (type == 'user') {
          return R.find(users, u => u.id === id);
        } else if (type == 'team') {
          return R.find(teams, t => t.id === id);
        } else {
          if (id.includes('user')) {
            return R.find(users, t => t.id.toString() === id.replace('user-', ''));
          } else {
            return R.find(teams, t => t.id.toString() === id.replace('team-', ''));
          }
        }
      }),
      R.ops.compact,
      R.ops.map(item => {
        return {
          id: item.id,
          title: R.get(item, 'label') || R.get(item, 'name') || CommonService.getFullName(item as CompactUserResponse),
        };
      }),
    );
  }, [fieldValue, additionalOptions, type, users, teams]);

  const items = useMemo(() => {
    const exceptUsers = users.filter(u => !fieldValue.includes(u.id));
    const exceptTeams = teams.filter(u => !fieldValue.includes(u.id));
    const exceptAdditional = additionalOptions.filter(u => !fieldValue.includes(u.value));

    const hasAdditionalTypes = additionalOptions.length > 0;

    if (type == 'both') {
      const inItems = [
        { label: 'Users', items: exceptUsers.map(i => ({ label: CommonService.getFullName(i), value: `user-${i.id}` })) },
        { label: 'Teams', items: teams.map(i => ({ label: i.name, value: `team-${i.id}` })) },
      ];
      if (hasAdditionalTypes) {
        inItems.push({ label: 'Other', items: exceptAdditional });
      }
      return CommonService.selectGroupedItems(inItems, 'label', s => s.items, 'label', 'value');
    }
    if (type === 'user') {
      if (hasAdditionalTypes) {
        return CommonService.selectGroupedItems(
          [
            { label: 'Users', items: exceptUsers.map(u => ({ label: CommonService.getFullName(u), value: u.id })) },
            { label: 'Other', items: exceptAdditional },
          ],
          'label',
          s => s.items,
          'label',
          'value',
        );
      }
      return CommonService.selectItems(exceptUsers, u => CommonService.getFullName(u), 'id');
    } else {
      if (hasAdditionalTypes) {
        return CommonService.selectGroupedItems(
          [
            { label: 'Teams', items: exceptTeams.map(t => ({ label: t.name, value: t.id })) },
            { label: 'Other', items: exceptAdditional },
          ],
          'label',
          s => s.items,
          'label',
          'value',
        );
      }
      return CommonService.selectItems(exceptTeams, t => t.name, 'id');
    }
  }, [users, teams, additionalOptions, type, fieldValue]);

  const content = (
    <React.Fragment>
      <AvatarGroup
        overlap
        size={2}
        items={selectedItems}
        tooltipContainer={props.tooltipContainer}
        viewOnly={props.viewOnly}
        onRemove={
          props.disabled
            ? undefined
            : (_, index) => {
                const newValues = fieldValue.filter((_, inIndex) => index !== inIndex);
                formControl.setValue(newValues);
                props.onChange && props.onChange(newValues);
              }
        }
      >
        {((props.maxSize && props.maxSize > fieldValue.length) || (!props.maxSize && canAdd)) && (
          <GCDropdown
            disabled={props.disabled}
            target={
              <div className="avatar-group-item" style={{ marginLeft: '-0.8rem' }} key={'_avatar_' + formControl.uid + 'new'}>
                <div className="d-inline-block" id={'user_item_new' + formControl.uid}>
                  <div className="avatar-md" style={{ width: '2rem', height: '2rem' }}>
                    <span
                      className={`avatar-title rounded-circle text-white font-size-12`}
                      style={{ backgroundColor: '#FFF', border: '1.5px dashed #3e3e3e' }}
                    >
                      <i style={{ color: '#3e3e3e' }} className="fa fa-user-plus"></i>
                    </span>
                  </div>
                </div>
              </div>
            }
          >
            {setOpen => (
              <Select
                autoFocus
                backspaceRemovesValue={false}
                components={{ IndicatorSeparator: null }}
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                isClearable={false}
                menuIsOpen
                options={items}
                placeholder="Search..."
                isSearchable
                // menuPortalTarget={document.body}
                styles={selectStyles}
                tabSelectsValue={false}
                onChange={value => {
                  if (value) {
                    if (fieldValue.length == 0 || closeOnSelect) {
                      setOpen(false);
                    }
                    props.onChange && props.onChange([...fieldValue, value.value]);
                    formControl.setValue([...fieldValue, value.value] as any);
                  }
                }}
              />
            )}
          </GCDropdown>
        )}
      </AvatarGroup>

      {!!fieldError && <FormHelperText style={{ color: '#f44336' }}>{fieldError}</FormHelperText>}
    </React.Fragment>
  );

  return content;
}
